<template lang="pug">
  .bg-neutral-300.py-5.px-8(style="min-height: calc(100dvh - 72px);")
    div(style="max-width:1600px; margin: 0 auto;")
      // Loading state
      template(v-if="isFetching")
        div(style="max-width: 1200px; margin: auto; padding: 40px;")
          claim-loading-state

      // Error state
      template(v-else-if="isError")
        div(style="max-width: 1200px; margin: auto; padding: 40px;")
          yz-empty-state-card(icon="$yzi-alert-triangle" :title="$t('panel.unable_to_load_claim_details')" :description="$t('panel.check_claim_rid_and_retry')" border-color="yz-danger-500" icon-color="yz-danger-500" :action-label="$t('retry')" @action-click="fetchClaimDetails")

      // View switching state
      template(v-else-if="isSwitchingView")
        claim-view-switching-state

      template(v-else)
        // Single Claim View
        view-single-claim(v-if="view === 'single'" :parent-claim="claim" @go-back="onGoBack" @updated="onClaimUpdated")

        // Bulk Claim View
        view-bulk-claim(v-if="view === 'bulk'" :parent-claim="claim" @go-back="onGoBack" @updated="onClaimUpdated" @switching-to-single="onSwitchingToSingle")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewSingleClaim from '@/components/clinic/panel/claims/single/ViewSingleClaim.vue'
import ViewBulkClaim from '@/components/clinic/panel/claims/bulk/ViewBulkClaim.vue'
import ClaimLoadingState from '@/components/clinic/panel/claims/ClaimLoadingState.vue'
import ClaimViewSwitchingState from '@/components/clinic/panel/claims/ClaimViewSwitchingState.vue'

export default {
  components: { ViewSingleClaim, ViewBulkClaim, ClaimLoadingState, ClaimViewSwitchingState },
  data() {
    return {
      // Page state
      isFetching: true,
      isError: false,
      initialized: false,
      isSwitchingView: false,
      // Data
      claim: null,
      view: null // 'single' or 'bulk'
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    claimId() {
      return this.$route.params.id || null
    }
  },
  mounted() {
    this.fetchClaimDetails()
  },
  beforeRouteEnter(to, from, next) {
    if (from.name) {
      to.meta.hasPrevPath = true
    }
    next()
  },
  methods: {
    ...mapActions(['showMessage']),
    onClaimUpdated(options) {
      if (options?.showLoading) {
        this.isFetching = true
      }
      this.fetchClaimDetails()
    },
    async fetchClaimDetails() {
      if (!this.claimId) {
        this.isError = true
        this.isFetching = false
        return
      }

      // Only show page-level loading on initial load
      if (!this.initialized) {
        this.isFetching = true
      }
      this.isError = false

      try {
        const { data } = await this.$axios.get(`/panels/${this.company.slug}/claims/${this.claimId}`)
        this.claim = data
        this.view = data.type // Set view based on claim type ('single' or 'bulk')
        this.initialized = true
      } catch (error) {
        console.error('Error fetching claim details:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    onGoBack() {
      if (this.$route?.meta?.hasPrevPath) {
        this.$router.back()
        return
      }
      // Fallback
      this.$router.push('/admin/clinic/panels/claims')
    },
    async onSwitchingToSingle() {
      // Show switching state
      this.isSwitchingView = true

      try {
        // Create a minimum delay promise
        const delayPromise = new Promise(resolve => setTimeout(resolve, 1000))

        // Fetch claim details and wait for both the API call and the delay
        await Promise.all([
          this.fetchClaimDetails(),
          delayPromise
        ])

        // The view will automatically switch because fetchClaimDetails updates claim.type to 'single'
      } catch (error) {
        console.error('Error during view switch:', error)
      } finally {
        this.isSwitchingView = false
      }
    }
  }
}
</script>

<style scoped></style>
